/**
 * The screen between signing up and having an organization.
 *
 * A brand-new account holds a token with no `org_id`, and every API route except one refuses it
 * (ADR 0021, ADR 0022). Without this the console signs someone in and then fails every request with
 * a 401 whose cause is invisible — the worst version of a first impression, because it looks like
 * the product is broken rather than unfinished.
 *
 * So: one call to `POST /v1/organization/bootstrap`, then a token refresh, because claims are
 * MINTED and not patched — the organization now exists, but the token in hand predates it, and only
 * a refresh makes the hook run again. Getting that order wrong is the bug this component exists to
 * not have: provisioning without refreshing leaves the user in exactly the state they started in,
 * with an organization they cannot see.
 *
 * This is interim, like the endpoint it calls. When the control Worker provisions at signup, the
 * account will already have an organization by the time it reaches the console and this whole path
 * becomes dead code to delete.
 */
import { Button, Callout, Card, ErrorState, LoadingState, Wordmark } from '@flowza/ui'
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { ApiError, api, apiConfigured } from '../api/client'
import { getClient } from '../supabase'

type State =
  | { readonly kind: 'working' }
  | { readonly kind: 'done' }
  | { readonly kind: 'unconfigured' }
  | { readonly kind: 'failed'; readonly message: string; readonly code: string }

export function ProvisionOrganization({ children }: { readonly children: ReactNode }) {
  const [state, setState] = useState<State>(() =>
    apiConfigured() ? { kind: 'working' } : { kind: 'unconfigured' },
  )
  const [attempt, setAttempt] = useState(0)

  const run = useCallback(async () => {
    // An invitation stashed by `/join` before it sent this person to sign up. Read and REMOVED
    // before the call, so a failure does not leave a live token sitting in session storage for the
    // next person on a shared machine — and so a retry that would fail the same way does not try
    // the token a second time.
    let invitationToken: string | null = null
    try {
      invitationToken = window.sessionStorage.getItem('flowza.invitation_token')
      window.sessionStorage.removeItem('flowza.invitation_token')
    } catch {
      // A browser refusing storage means there is no invitation to find, which is the ordinary case.
    }

    try {
      await api('/v1/organization/bootstrap', {
        method: 'POST',
        // The ORDINARY body when there is no invitation: the shape this call has always sent.
        ...(invitationToken === null ? {} : { body: { invitation_token: invitationToken } }),
      })
      // Somebody who JOINED a team is a new person in an existing organization, and the four facts
      // the profile wizard asks for are what make their first signature quick. Somebody creating
      // their own organization lands on the dashboard as before: they have no colleagues waiting
      // and the dashboard's own row offers the wizard whenever they want it.
      //
      // A flag rather than a navigation here, because this component unmounts the moment the
      // refreshed session re-renders the gate above it — a redirect issued from inside it races
      // that re-render. `Dashboard` reads the flag once and clears it.
      if (invitationToken !== null) {
        try {
          window.sessionStorage.setItem('flowza.just_joined', '1')
        } catch {
          // No storage means no redirect, which costs the person one click on the dashboard row.
        }
      }
    } catch (error) {
      // An invitation that will not open is the user's to see, not a setup failure to retry. It
      // arrives as 403 `invitation_not_open` carrying the definer's own words — expired, withdrawn,
      // already used, or sent to a different address — and those words are chosen to leak nothing
      // about invitations that are not theirs, so they are shown rather than reworded.
      //
      // Falling through to the personal-org branch would be worse than an error: someone who
      // clicked a colleague's invitation would silently get a workspace of their own instead, and
      // would have no way to tell that they had not joined the team.
      if (error instanceof ApiError && error.code === 'invitation_not_open') throw error

      // `409 already_provisioned` is not a failure HERE, and treating it as one is a dead end.
      //
      // The API refuses bootstrap when the presented token ALREADY carries `org_id` — a sensible
      // answer to "give me an organization" from someone who has one. But that is not what this
      // component is asking. Its contract is "make sure this account HAS an organization", and a
      // 409 says it does. The only reason this screen is mounted at all is that
      // `needsOrganization` was read off the token at render time, and Supabase refreshes sessions
      // on its own timer — so a token can gain the claim between that render and this call, which
      // turns a fully provisioned account into "We could not finish setting up your account".
      //
      // It never recovers on its own, either: Try again re-reads the same claim-bearing token and
      // gets the same 409 forever, so the only way out is signing out. Falling through to the
      // refresh below is what this state actually needs.
      if (!(error instanceof ApiError && error.code === 'already_provisioned')) throw error
    }
    // The claims are minted at refresh, not patched into the token we hold. Without this the
    // organization exists and the session still cannot see it.
    const client = getClient()
    if (client === null) throw new Error('no Supabase client')
    const { error } = await client.auth.refreshSession()
    if (error) throw error
  }, [])

  useEffect(() => {
    if (!apiConfigured()) return
    let cancelled = false
    setState({ kind: 'working' })
    run()
      .then(() => {
        // No state change on success: the refreshed session re-renders the gate above this, which
        // stops rendering this component at all. Setting 'done' here would be a state nobody sees.
        if (!cancelled) setState({ kind: 'done' })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        setState({
          kind: 'failed',
          code: error instanceof ApiError ? error.code : 'unknown',
          message: error instanceof Error ? error.message : 'setting up your account did not work',
        })
      })
    return () => {
      cancelled = true
    }
  }, [attempt, run])

  if (state.kind === 'done') return <>{children}</>

  return (
    <main className="bg-surface-base flex min-h-screen flex-col items-center justify-center px-4">
      <div className="w-full max-w-md">
        <Wordmark size="sm" className="mb-6" />
        {state.kind === 'unconfigured' ? (
          <Callout tone="warning" title="This deployment has no API">
            Your account exists, but setting up its organization needs the api Worker. Until{' '}
            <code>VITE_API_URL</code> points at one, the console cannot finish signing you in.
          </Callout>
        ) : state.kind === 'working' ? (
          <Card title="Setting up your workspace">
            <LoadingState label="Creating your organization" />
            <p className="text-text-muted mb-0 mt-3 text-sm">
              This happens once, the first time you sign in.
            </p>
          </Card>
        ) : (
          <ErrorState
            title={
              state.code === 'invitation_not_open'
                ? 'That invitation cannot be used'
                : 'We could not finish setting up your account'
            }
            body={
              state.code === 'region_mismatch'
                ? 'Your account asked for a region this server does not serve. This is ours to fix, not yours — please get in touch.'
                : state.code === 'invitation_not_open'
                  ? // The API's own sentence: expired, withdrawn, already used, or sent to a
                    // different address. Not reworded, because it is worded to leak nothing.
                    `${state.message} Ask whoever invited you to send a new one — and sign up with the address they invited.`
                  : `${state.message} (${state.code})`
            }
            onRetry={() => setAttempt((n) => n + 1)}
          />
        )}
        {state.kind === 'failed' ? (
          <p className="text-text-muted mt-4 text-sm">
            <Button variant="secondary" onClick={() => void getClient()?.auth.signOut()}>
              Sign out
            </Button>
          </p>
        ) : null}
      </div>
    </main>
  )
}
